/* eslint-disable max-lines-per-function */
import { Icon20Cancel } from '@vkontakte/icons';
import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { sendXray } from 'reactApp/utils/ga';

import { sendPromoBlockDWH } from './PromoBlock.analytics';
import styles from './PromoBlock.css';
import { type PromoBlockProps, PromoBlockButtonColor } from './PromoBlock.types';

const sendRadar = (action: 'view' | 'click' | 'click-second' | 'close' | 'click-secondary', analyticsName?: string) => {
    if (!analyticsName) {
        return;
    }
    sendXray(['autoupload_promo', 'touch', action]);
    sendPromoBlockDWH(action, analyticsName, 'files');
};

export const PromoBlock = memo<PromoBlockProps>(
    ({
        id,
        title,
        text,
        buttonText,
        textColor,
        buttonSecondaryText,
        buttonSecondaryColor = PromoBlockButtonColor.white,
        backgroundColor,
        buttonColor,
        image,
        imageFullHeight,
        analyticsName,
        showOnce = true,
        onClose,
        onClick,
        onSecondaryClick,
        onShow,
        closable = true,
        buttonDisabled = false,
        secondaryButtonAsLink = false,
        closeTransparent = false,
        isRedirectToAppPromo = false,
    }) => {
        const dispatch = useDispatch();
        const [visible, setVisible] = useState(true);
        const isCorp = useSelector(UserSelectors.isCorpUser);
        const isBiz = useSelector(UserSelectors.isBizUser);
        const isClosed = Boolean(dispatch(UserStorageActions.get(id)));
        const hideBlock = !visible || isClosed || (isCorp && !isRedirectToAppPromo) || isBiz;

        const handleClose = () => {
            onClose?.();
            setVisible(false);
            if (showOnce) {
                dispatch(UserStorageActions.set(id));
            }
            sendRadar('close', analyticsName);
        };

        const handleClick = () => {
            onClick();
            if (showOnce) {
                dispatch(UserStorageActions.set(id));
            }
            sendRadar('click', analyticsName);
        };

        const handleSecondaryClick = () => {
            onSecondaryClick?.();

            if (showOnce) {
                dispatch(UserStorageActions.set(id));
            }

            sendRadar('click-secondary', analyticsName);
        };

        useEffect(() => {
            sendRadar('view', analyticsName);
        }, [analyticsName]);

        useEffect(() => {
            if (hideBlock) {
                return;
            }

            onShow?.();
        }, [onShow, hideBlock]);

        if (hideBlock) {
            return null;
        }

        return (
            <div
                className={classNames(styles.root, {
                    [styles[`rootBg_${backgroundColor}`]]: !!backgroundColor,
                    [styles[`rootText_${textColor}`]]: !!textColor,
                })}
            >
                {closable && (
                    <div
                        className={classNames(styles.close, {
                            [styles.close_transparent]: closeTransparent,
                        })}
                        onClick={handleClose}
                    >
                        <Icon20Cancel />
                    </div>
                )}
                <div className={classNames(styles.content, { [styles.content_noImage]: !image })}>
                    {title && (
                        <>
                            <Text weight="2" className={styles.title}>
                                {title}
                            </Text>
                            <Spacing size={isRebranding ? 4 : 8} />
                        </>
                    )}
                    <Text className={styles.text}>{text}</Text>
                    <Spacing size={isRedirectToAppPromo ? 8 : 12} />
                    <div className={styles.buttonBlock}>
                        <Button
                            sizeMode={ButtonSizeMode.small}
                            theme="vk"
                            onClick={handleClick}
                            className={classNames(styles.button, {
                                [styles[`button_${buttonColor}`]]: !!buttonColor,
                            })}
                            disabled={buttonDisabled}
                        >
                            {buttonText}
                        </Button>
                        {Boolean(buttonSecondaryText) &&
                            (secondaryButtonAsLink ? (
                                <div className={styles.secondaryButton} onClick={handleSecondaryClick}>
                                    {buttonSecondaryText}
                                </div>
                            ) : (
                                <Button
                                    sizeMode={ButtonSizeMode.small}
                                    theme="vk"
                                    onClick={handleSecondaryClick}
                                    className={classNames(styles.button, {
                                        [styles[`button_${buttonSecondaryColor}`]]: !!buttonSecondaryColor,
                                    })}
                                    disabled={buttonDisabled}
                                >
                                    {buttonSecondaryText}
                                </Button>
                            ))}
                    </div>
                </div>
                {!!image && (
                    <img
                        src={image}
                        alt={typeof title === 'string' ? title : ''}
                        className={classNames(styles.image, {
                            [styles['image_fullHeight']]: imageFullHeight,
                            [styles['image_redirectToApp']]: isRedirectToAppPromo,
                        })}
                    />
                )}
            </div>
        );
    }
);

PromoBlock.displayName = 'PromoBlock';
